import { PaneChannelTimings } from './AnimationTimingTypes';
import { ArrayLikeUtils } from './ArrayLikeUtils';
import { DataStoreValue, deriveWarpedFraction, getAttributeTiming } from './DataStorePaneStateUtility';
import { DataStorePaths } from './DataStorePaths';
import {
  buildEnteringToIndices,
  fixPrevNextForPane,
  interpolateLabelEncodings,
  interpolatePathWithPerIndexFractions,
  PaneInterpolationContext,
} from './PaneInterpolation';
import { PaneStateAttribute, PaneStateAttributeUtil } from './PaneStateAttribute';

export class ChannelTimedPaneInterpolation {
  interpolatePane(
    context: PaneInterpolationContext,
    paneId: string,
    targetGetValue: (path: string) => DataStoreValue,
    globalFraction: number,
    channelTimings: PaneChannelTimings,
    exitingToIndices?: Set<number>
  ): void {
    const markIdPath = `PDMarks.${paneId}.Encodings.MarkId`;
    const fromMarkIds = context.getValue(markIdPath);
    const toMarkIds = targetGetValue(markIdPath);
    const indexMapping = context.buildMarkIdIndexMapping(fromMarkIds, toMarkIds);
    const enteringToIndices = buildEnteringToIndices(toMarkIds, indexMapping);
    const toArr = ArrayLikeUtils.toArray<number>(toMarkIds);

    for (const attribute of PaneStateAttributeUtil.getAllMarkAttributes()) {
      const path = DataStorePaths.getKeyPathForAttribute(paneId, attribute);
      if (!path) {
        continue;
      }

      const enterTiming = getAttributeTiming(attribute, channelTimings.enterTiming, channelTimings.enterAttributeTimingOverrides);
      const moveTiming = getAttributeTiming(attribute, channelTimings.moveTiming, channelTimings.moveAttributeTimingOverrides);
      const exitTiming = getAttributeTiming(attribute, channelTimings.exitTiming, channelTimings.exitAttributeTimingOverrides);
      const enterFraction = deriveWarpedFraction(globalFraction, enterTiming);
      const moveFraction = deriveWarpedFraction(globalFraction, moveTiming);
      const exitFraction = deriveWarpedFraction(globalFraction, exitTiming);
      const fractionForToIndex = new Map<number, number>();

      for (let toIdx = 0; toIdx < toArr.length; toIdx++) {
        const markId = toArr[toIdx];
        if (typeof markId === 'number' && channelTimings.enterMarkIdSet.has(markId)) {
          fractionForToIndex.set(toIdx, enterFraction);
        } else if (typeof markId === 'number' && channelTimings.moveMarkIdSet.has(markId)) {
          fractionForToIndex.set(toIdx, moveFraction);
        } else if (typeof markId === 'number' && channelTimings.exitMarkIdSet.has(markId)) {
          fractionForToIndex.set(toIdx, exitFraction);
        } else {
          fractionForToIndex.set(toIdx, moveFraction);
        }
      }

      interpolatePathWithPerIndexFractions(
        context,
        targetGetValue,
        path,
        attribute,
        indexMapping,
        fractionForToIndex,
        enteringToIndices,
        exitingToIndices
      );
    }

    interpolateLabelEncodings(
      context,
      paneId,
      targetGetValue,
      (attribute: PaneStateAttribute) =>
        deriveWarpedFraction(
          globalFraction,
          getAttributeTiming(attribute, channelTimings.moveTiming, channelTimings.moveAttributeTimingOverrides)
        ),
      channelTimings.moveLabelMarkIdSet
    );
    fixPrevNextForPane(context, paneId);
  }
}
